import {
  ArrowDownward,
  ArrowUpward,
  Archive as ArchiveIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  ExpandLess,
  ExpandMore,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  NoteAdd as NoteIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  RestoreFromTrash as RestoreIcon,
  Search as SearchIcon,
  Unarchive as UnarchiveIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import Currency from "../../components/common/Currency";
import NoTranslate from "../../components/common/NoTranslate";
import OrderDetailsModal from "../../components/backend/orders/OrderDetailsModal";
import OrderStatusUpdateModal from "../../components/backend/orders/OrderStatusUpdateModal";
import PaymentStatusUpdateModal from "../../components/backend/orders/PaymentStatusUpdateModal";
import { useCargoCompanies } from "../../hooks/useCargoCompany";
import { adminOrderItemService } from "../../services/adminOrderItemService";
import { adminOrderService } from "../../services/adminOrderService";
import {
  AdminOrderDetail,
  AdminOrderFilters,
  AdminOrderList,
  AdminOrderStatistics,
  OrderStatusDto,
  PAYMENT_STATUSES,
  UpdateOrderComprehensiveRequest,
} from "../../types/adminOrders";
import { ItemStatus } from "../../types/admin";

import AdminPage from "./AdminPage";

// Relative time formatter (i18n-friendly)
const getRelativeTime = (dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string, timeStr?: string): string => {
  const dateString = timeStr ? `${dateStr} ${timeStr}` : dateStr;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateStr;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t("admin.orders.justNow");
  if (diffMins < 60) return t("admin.orders.minutesAgo", { count: diffMins });
  if (diffHours < 24) return t("admin.orders.hoursAgo", { count: diffHours });
  if (diffDays < 7) return t("admin.orders.daysAgo", { count: diffDays });
  if (diffDays < 30) return t("admin.orders.weeksAgo", { count: Math.floor(diffDays / 7) });
  return dateStr;
};

// CSV export helper
const exportOrdersToCSV = (orders: AdminOrderList[], t: (key: string) => string) => {
  const headers = [
    t("admin.orders.csvOrderNumber"), t("admin.orders.customer"), t("admin.orders.customerEmail"),
    t("admin.orders.status"), t("admin.orders.paymentStatus"),
    t("admin.orders.csvPaymentMethod"), t("admin.orders.csvTotal"), t("admin.orders.shipping"),
    t("admin.orders.tax"), t("admin.orders.fees"), t("admin.orders.csvFinalTotal"),
    t("admin.orders.csvCargoCompany"), t("admin.orders.items"), t("admin.orders.orderedDate"),
  ];
  const rows = orders.map((o) => [
    o.orderNumber, o.fullName, o.email, o.status, o.paymentStatus,
    o.paymentMethodType || "", o.totalPrice, o.shippingCost, o.tax,
    o.fees, o.finalTotal, o.cargoCompanyName || "", o.itemCount,
    `${o.orderedDate} ${o.orderedDateTime}`,
  ]);
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// Parse URL search params into AdminOrderFilters
const parseFiltersFromParams = (params: URLSearchParams): AdminOrderFilters => {
  const filters: AdminOrderFilters = {
    sortBy: params.get("sortBy") || "createdat",
    sortDescending: params.get("sortDescending") !== "false",
    pageNumber: params.get("pageNumber") ? parseInt(params.get("pageNumber")!, 10) : 1,
    pageSize: params.get("pageSize") ? parseInt(params.get("pageSize")!, 10) : 20,
  };
  if (params.get("search")) filters.search = params.get("search")!;
  if (params.get("status")) filters.status = params.get("status")!;
  if (params.get("paymentStatus")) filters.paymentStatus = params.get("paymentStatus")!;
  if (params.get("startDate")) filters.startDate = params.get("startDate")!;
  if (params.get("endDate")) filters.endDate = params.get("endDate")!;
  if (params.get("minAmount")) filters.minAmount = parseFloat(params.get("minAmount")!);
  if (params.get("maxAmount")) filters.maxAmount = parseFloat(params.get("maxAmount")!);
  return filters;
};

// Serialize filters to URL search params
const filtersToParams = (filters: AdminOrderFilters): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.status) params.set("status", filters.status);
  if (filters.paymentStatus) params.set("paymentStatus", filters.paymentStatus);
  if (filters.startDate) params.set("startDate", filters.startDate);
  if (filters.endDate) params.set("endDate", filters.endDate);
  if (filters.minAmount !== undefined) params.set("minAmount", filters.minAmount.toString());
  if (filters.maxAmount !== undefined) params.set("maxAmount", filters.maxAmount.toString());
  if (filters.sortBy && filters.sortBy !== "createdat") params.set("sortBy", filters.sortBy);
  if (filters.sortDescending === false) params.set("sortDescending", "false");
  if (filters.pageNumber && filters.pageNumber > 1) params.set("pageNumber", filters.pageNumber.toString());
  if (filters.pageSize && filters.pageSize !== 20) params.set("pageSize", filters.pageSize.toString());
  return params;
};

const OrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get cargo companies for the edit order dialog
  const { data: cargoCompanies } = useCargoCompanies({ activeOnly: true });

  // Initialize filters from URL params
  const initialFilters = parseFiltersFromParams(searchParams);
  const [page, setPage] = useState((initialFilters.pageNumber || 1) - 1);
  const [rowsPerPage, setRowsPerPage] = useState(initialFilters.pageSize || 20);
  const [orders, setOrders] = useState<AdminOrderList[]>([]);
  const [statistics, setStatistics] = useState<AdminOrderStatistics | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter states — initialized from URL
  const [filters, setFilters] = useState<AdminOrderFilters>(initialFilters);

  // Sync filters to URL (replace, don't push — avoids polluting browser history)
  useEffect(() => {
    const params = filtersToParams(filters);
    setSearchParams(params, { replace: true });
  }, [filters]);

  // UI states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null);
  const hasActiveUrlFilters = !!(initialFilters.search || initialFilters.status || initialFilters.paymentStatus || initialFilters.pickUpLocation || initialFilters.startDate || initialFilters.endDate || initialFilters.minAmount !== undefined || initialFilters.maxAmount !== undefined);
  const [showFilters, setShowFilters] = useState(hasActiveUrlFilters);
  const [showStats, setShowStats] = useState(() => {
    const saved = localStorage.getItem("admin.orders.showStats");
    return saved !== null ? saved === "true" : true;
  });
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [noteText, setNoteText] = useState("");
  const hasActiveFilters = !!(filters.search || filters.status || filters.paymentStatus || filters.pickUpLocation || filters.startDate || filters.endDate || filters.minAmount !== undefined || filters.maxAmount !== undefined);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Dialogs
  const [statusDialog, setStatusDialog] = useState<{
    open: boolean;
    orderNumber: string;
    type: "status" | "payment" | "assign" | "note";
  }>({
    open: false,
    orderNumber: "",
    type: "status",
  });

  // Order status update modal
  const [orderStatusModal, setOrderStatusModal] = useState<{
    open: boolean;
    orderId: string;
    orderNumber: string;
    currentStatus: string;
    orderItems: AdminOrderDetail["items"];
  }>({
    open: false,
    orderId: "",
    orderNumber: "",
    currentStatus: "",
    orderItems: [],
  });

  // Comprehensive order update dialog
  const [editOrderDialog, setEditOrderDialog] = useState<{
    open: boolean;
    order: AdminOrderDetail | null;
    originalOrder: AdminOrderDetail | null;
    loading: boolean;
    error: string | null;
  }>({
    open: false,
    order: null,
    originalOrder: null,
    loading: false,
    error: null,
  });

  // Order details modal state
  const [orderDetailsModal, setOrderDetailsModal] = useState<{
    open: boolean;
    orderNumber: string | null;
    orderId: string | null;
  }>({
    open: false,
    orderNumber: null,
    orderId: null,
  });

  // Payment status update modal state
  const [paymentStatusModal, setPaymentStatusModal] = useState<{
    open: boolean;
    order: AdminOrderList | null;
  }>({
    open: false,
    order: null,
  });

  // Delete/Archive confirmation dialog
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; orderId: string; orderNumber: string; loading: boolean }>({ open: false, orderId: "", orderNumber: "", loading: false });
  const [archiveConfirm, setArchiveConfirm] = useState<{ open: boolean; orderId: string; orderNumber: string; loading: boolean }>({ open: false, orderId: "", orderNumber: "", loading: false });

  // Order statuses from server
  const [orderStatuses, setOrderStatuses] = useState<OrderStatusDto[]>([]);

  // Search debounce
  const [searchTerm, setSearchTerm] = useState(initialFilters.search || "");
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Bulk selection
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [bulkStatusId, setBulkStatusId] = useState<number | "">("");
  const [bulkItemStatusId, setBulkItemStatusId] = useState<number | "">("");
  const [bulkUpdateItems, setBulkUpdateItems] = useState(false);
  const [bulkSendNotification, setBulkSendNotification] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [itemStatuses, setItemStatuses] = useState<ItemStatus[]>([]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setSelectedOrderIds([]);
    setFilters((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);
    setPage(0);
    setSelectedOrderIds([]);
    setFilters((prev) => ({ ...prev, pageSize: newPageSize, pageNumber: 1 }));
  };

  // Load order statuses and item statuses once on mount
  useEffect(() => {
    loadOrderStatuses();
    loadItemStatuses();
  }, []);

  // Lazy-load statistics only when stats section is expanded
  const statsLoadedRef = useRef(false);
  useEffect(() => {
    if (showStats && !statsLoadedRef.current) {
      statsLoadedRef.current = true;
      loadStatistics();
    }
  }, [showStats]);

  // Load orders when filters change, with race condition guard
  const loadRequestRef = useRef(0);
  useEffect(() => {
    const requestId = ++loadRequestRef.current;
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await adminOrderService.getOrders(filters);
        // Only update state if this is still the latest request
        if (requestId === loadRequestRef.current) {
          setOrders(response.items);
          setTotalCount(response.totalCount);
          setTotalPages(response.totalPages);
        }
      } catch (error) {
        if (requestId === loadRequestRef.current) {
          showSnackbar(t("admin.orders.errorLoadingOrders"), "error");
        }
      } finally {
        if (requestId === loadRequestRef.current) {
          setLoading(false);
        }
      }
    };
    fetchOrders();
  }, [filters]);

  const loadOrders = async () => {
    const requestId = ++loadRequestRef.current;
    try {
      setLoading(true);
      const response = await adminOrderService.getOrders(filters);
      if (requestId === loadRequestRef.current) {
        setOrders(response.items);
        setTotalCount(response.totalCount);
        setTotalPages(response.totalPages);
      }
    } catch (error) {
      if (requestId === loadRequestRef.current) {
        showSnackbar(t("admin.orders.errorLoadingOrders"), "error");
      }
    } finally {
      if (requestId === loadRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await adminOrderService.getOrderStatistics();
      setStatistics(stats);
    } catch (error) {}
  };

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleRefresh = () => {
    loadOrders();
    if (showStats) loadStatistics();
  };

  const handleFilterChange = (key: keyof AdminOrderFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, pageNumber: 1 }));
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      handleFilterChange("search", value);
    }, 400);
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // Persist showStats to localStorage
  useEffect(() => {
    localStorage.setItem("admin.orders.showStats", String(showStats));
  }, [showStats]);

  // Keyboard shortcut: Ctrl+K or / to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey && e.key === "k") || (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes((e.target as HTMLElement)?.tagName))) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const clearFilters = () => {
    setSearchTerm("");
    setFilters({
      pageNumber: 1,
      pageSize: rowsPerPage,
      sortBy: "createdat",
      sortDescending: true,
    });
  };

  // Sort handler for clickable column headers
  const handleSort = (backendKey: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: backendKey,
      sortDescending: prev.sortBy === backendKey ? !prev.sortDescending : true,
      pageNumber: 1,
    }));
    setPage(0);
  };

  // Remove a single active filter (for filter chips)
  const handleRemoveFilter = (key: keyof AdminOrderFilters) => {
    if (key === "search") setSearchTerm("");
    setFilters((prev) => {
      const next = { ...prev, pageNumber: 1 };
      delete next[key];
      return next;
    });
  };

  // Get display label for a filter value
  const getFilterLabel = useCallback(
    (key: string, value: string | number): string => {
      if (key === "status") {
        const status = orderStatuses.find((s) => s.id.toString() === value);
        return status?.displayName || String(value);
      }
      if (key === "minAmount" || key === "maxAmount") return `$${value}`;
      return String(value);
    },
    [orderStatuses]
  );

  // Bulk selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedOrderIds(orders.map((o) => o.orderNumber));
    } else {
      setSelectedOrderIds([]);
    }
  };

  const handleSelectOrder = (orderNumber: string) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderNumber)
        ? prev.filter((id) => id !== orderNumber)
        : [...prev, orderNumber]
    );
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatusId || selectedOrderIds.length === 0) return;
    if (bulkUpdateItems && !bulkItemStatusId) return;
    try {
      setBulkLoading(true);
      const statusName = orderStatuses.find((s) => s.id === bulkStatusId)?.name || "";
      await adminOrderService.bulkUpdateOrderStatus({
        orderNumbers: selectedOrderIds,
        status: statusName,
        itemStatusId: bulkUpdateItems && bulkItemStatusId ? Number(bulkItemStatusId) : undefined,
        sendNotification: bulkSendNotification || undefined,
      });
      showSnackbar(
        t("admin.orders.bulkOrderStatusUpdateSuccess", { count: selectedOrderIds.length }),
        "success"
      );
      setSelectedOrderIds([]);
      setBulkStatusId("");
      setBulkItemStatusId("");
      setBulkUpdateItems(false);
      setBulkSendNotification(false);
      await loadOrders();
      if (showStats) await loadStatistics();
    } catch (error) {
      showSnackbar(t("admin.orders.bulkOrderStatusUpdateFailed"), "error");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    orderId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrder(orderId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedOrder(null);
  };

  const handleArchiveOrder = async (orderId: string) => {
    try {
      await adminOrderService.archiveOrder(orderId);
      showSnackbar(t("admin.orders.orderArchived"), "success");
      await loadOrders();
      if (showStats) await loadStatistics();
    } catch {
      showSnackbar(t("admin.orders.errorArchivingOrder"), "error");
    }
  };

  const handleUnarchiveOrder = async (orderId: string) => {
    try {
      await adminOrderService.unarchiveOrder(orderId);
      showSnackbar(t("admin.orders.orderUnarchived"), "success");
      await loadOrders();
      if (showStats) await loadStatistics();
    } catch {
      showSnackbar(t("admin.orders.errorUnarchivingOrder"), "error");
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    try {
      await adminOrderService.deleteOrder(orderId);
      showSnackbar(t("admin.orders.orderDeleted"), "success");
      await loadOrders();
      if (showStats) await loadStatistics();
    } catch {
      showSnackbar(t("admin.orders.errorDeletingOrder"), "error");
    }
  };

  const handleRestoreOrder = async (orderId: string) => {
    try {
      await adminOrderService.restoreOrder(orderId);
      showSnackbar(t("admin.orders.orderRestored"), "success");
      await loadOrders();
      if (showStats) await loadStatistics();
    } catch {
      showSnackbar(t("admin.orders.errorRestoringOrder"), "error");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Delivered":
        return "success";
      case "Shipped":
        return "primary";
      case "Processing":
        return "warning";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    return status === "Paid" ? "success" : "warning";
  };

  const handleStatusClick = async (orderId: string, orderNumber: string, currentStatus: string) => {
    // Open modal without pre-fetching items - the modal will fetch them when needed
    setOrderStatusModal({
      open: true,
      orderId,
      orderNumber,
      currentStatus,
      orderItems: [],
    });
  };

  const handleOrderStatusUpdate = async (
    orderId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean,
    selectedItemIds?: string[],
    itemStatusId?: number
  ) => {
    try {
      await adminOrderService.updateOrderStatus(orderId, {
        newStatusId,
        statusChangeNotes: notes,
        sendNotification: sendNotification ?? true,
        orderItemIds: selectedItemIds,
        itemStatusId,
      });

      // Reload orders to get updated status
      await loadOrders();
      showSnackbar(t("admin.orders.orderStatusUpdated"), "success");
    } catch (error) {
      showSnackbar(t("admin.orders.failedUpdateOrderStatus"), "error");
      throw error;
    }
  };

  const handleEditOrder = async (order: AdminOrderList) => {
    // Open modal immediately for better UX
    setEditOrderDialog({
      open: true,
      order: null,
      originalOrder: null,
      loading: true,
      error: null,
    });

    try {
      // Fetch full order details from API using orderId
      const fullOrderDetails = await adminOrderService.getOrderDetail(order.id);

      // Populate dialog with complete order data
      setEditOrderDialog({
        open: true,
        order: fullOrderDetails,
        originalOrder: fullOrderDetails, // Store original for comparison
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error("Error loading order details for edit:", error);
      setEditOrderDialog({
        open: true,
        order: null,
        originalOrder: null,
        loading: false,
        error: t("admin.orders.failedLoadOrderDetails"),
      });
    }
  };

  const handleCloseEditDialog = () => {
    setEditOrderDialog({
      open: false,
      order: null,
      originalOrder: null,
      loading: false,
      error: null,
    });
  };

  // Order details modal handlers
  const handleViewOrderDetails = (orderNumber: string, orderId: string) => {
    setOrderDetailsModal({
      open: true,
      orderNumber: orderNumber,
      orderId: orderId,
    });
  };

  const handleCloseOrderDetailsModal = () => {
    setOrderDetailsModal({
      open: false,
      orderNumber: null,
      orderId: null,
    });
  };

  const handleSaveOrderEdit = async (updatedOrder: AdminOrderDetail) => {
    try {
      // Prepare the comprehensive update request
      const updateRequest: UpdateOrderComprehensiveRequest = {};

      // Compare against original order values, not current dialog values
      const originalOrder = editOrderDialog.originalOrder!;

      // Add payment status update if changed
      // Use paymentInfo.paymentStatus as fallback if paymentStatus is not set
      const currentPaymentStatus =
        updatedOrder.paymentStatus ||
        updatedOrder.paymentInfo?.paymentStatus ||
        "";
      const originalPaymentStatus =
        originalOrder?.paymentStatus ||
        originalOrder?.paymentInfo?.paymentStatus ||
        "";

      if (currentPaymentStatus !== originalPaymentStatus) {
        updateRequest.paymentStatus = currentPaymentStatus;
      }

      // Add new fields - compare against original values
      if (
        updatedOrder.totalCarrierCargoFee !==
        originalOrder?.totalCarrierCargoFee
      ) {
        updateRequest.totalCarrierCargoFee =
          updatedOrder.totalCarrierCargoFee ?? undefined;
      }

      if (updatedOrder.pickupLocation !== originalOrder?.pickupLocation) {
        updateRequest.pickupLocation = updatedOrder.pickupLocation;
      }

      // Add notes if changed
      if (updatedOrder.notes !== originalOrder?.notes) {
        updateRequest.notes = updatedOrder.notes;
      }

      // Add cargo company if changed
      // Normalize values for comparison (handle undefined, null, and empty string)
      // Convert empty string/null to undefined for consistent comparison (AdminOrderDetail uses string | undefined)
      const normalizeCargoCompanyId = (
        id: string | undefined | null
      ): string | undefined => {
        if (!id || (typeof id === "string" && id.trim() === ""))
          return undefined;
        return id;
      };

      const currentCargoCompanyId = normalizeCargoCompanyId(
        updatedOrder.cargoCompanyId
      );
      const originalCargoCompanyId = normalizeCargoCompanyId(
        originalOrder?.cargoCompanyId
      );

      // Only update if there's an actual change
      if (currentCargoCompanyId !== originalCargoCompanyId) {
        // Backend expects string | null, so convert undefined to null
        updateRequest.cargoCompanyId = currentCargoCompanyId || null;
      }

      // Call the comprehensive update method
      await adminOrderService.updateOrderComprehensive(
        updatedOrder.id,
        updateRequest
      );
      showSnackbar(t("admin.orders.orderUpdated"), "success");
      handleCloseEditDialog();
      await loadOrders(); // Reload orders to get updated data
    } catch (error) {
      console.error("Error updating order:", error);
      showSnackbar(t("admin.orders.failedUpdateOrder"), "error");
    }
  };

  // Load order statuses from server
  const loadOrderStatuses = async () => {
    try {
      const statuses = await adminOrderService.getOrderStatuses();
      setOrderStatuses(statuses);
    } catch (error) {
      console.error("Error loading order statuses:", error);
      showSnackbar(t("admin.orders.failedLoadOrderStatuses"), "error");
    }
  };

  // Load item statuses from server
  const loadItemStatuses = async () => {
    try {
      const statuses = await adminOrderItemService.getAvailableItemStatuses();
      setItemStatuses(statuses);
    } catch (error) {
      console.error("Error loading item statuses:", error);
    }
  };

  return (
    <AdminPage>
      {/* Blocking overlay during bulk update */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: "column", gap: 2 }}
        open={bulkLoading}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography variant="h6" color="inherit">
          {t("admin.orders.bulkUpdating", { count: selectedOrderIds.length })}
        </Typography>
        <Typography variant="body2" color="inherit">
          {t("admin.orders.pleaseWait")}
        </Typography>
      </Backdrop>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography variant="h4">{t("admin.orders.title")}</Typography>
            {!loading && (
              <Typography variant="subtitle1" color="text.secondary">
                ({totalCount})
              </Typography>
            )}
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ mr: 1 }}
            >
              {t("admin.orders.filters")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ mr: 1 }}
            >
              {t("admin.orders.refresh")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={() => exportOrdersToCSV(orders, t)}
              disabled={orders.length === 0}
            >
              {t("admin.orders.exportOrders")}
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards - Collapsible */}
        <Box display="flex" alignItems="center" mb={1}>
          <Tooltip title={showStats ? t("admin.orders.hideStats") : t("admin.orders.showStats")}>
            <IconButton size="small" onClick={() => setShowStats(!showStats)}>
              {showStats ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Tooltip>
          <Typography variant="subtitle2" color="textSecondary" sx={{ cursor: "pointer" }} onClick={() => setShowStats(!showStats)}>
            {showStats ? t("admin.orders.hideStats") : t("admin.orders.showStats")}
          </Typography>
        </Box>
        <Collapse in={showStats}>
        {statistics && (
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {t("admin.orders.totalOrders")}
                  </Typography>
                  <Typography variant="h4">{statistics.totalOrders}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {t("admin.orders.pendingOrders")}
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {statistics.pendingOrders}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {t("admin.orders.totalRevenue")}
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    <NoTranslate as="span">
                      ${statistics.totalRevenue.toLocaleString()}
                    </NoTranslate>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    {t("admin.orders.avgOrderValue")}
                  </Typography>
                  <Typography variant="h4">
                    <NoTranslate as="span">
                      ${statistics.averageOrderValue.toFixed(2)}
                    </NoTranslate>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
        </Collapse>

        {/* Advanced Filters */}
        {showFilters && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t("admin.orders.status")}</InputLabel>
                  <Select
                    value={filters.status || ""}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    label={t("admin.orders.status")}
                  >
                    <MenuItem value="">{t("admin.orders.all")}</MenuItem>
                    {orderStatuses.map((status) => (
                      <MenuItem key={status.id} value={status.id.toString()}>
                        {status.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t("admin.orders.paymentStatus")}</InputLabel>
                  <Select
                    value={filters.paymentStatus || ""}
                    onChange={(e) =>
                      handleFilterChange("paymentStatus", e.target.value)
                    }
                    label={t("admin.orders.paymentStatus")}
                  >
                    <MenuItem value="">{t("admin.orders.all")}</MenuItem>
                    {PAYMENT_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t("admin.orders.pickupLocation")}</InputLabel>
                  <Select
                    value={filters.pickUpLocation || ""}
                    onChange={(e) =>
                      handleFilterChange("pickUpLocation", e.target.value || undefined)
                    }
                    label={t("admin.orders.pickupLocation")}
                  >
                    <MenuItem value="">{t("admin.orders.all")}</MenuItem>
                    <MenuItem value="Dushanbe">{t("checkout.dushanbe")}</MenuItem>
                    <MenuItem value="Khujand">{t("checkout.khujand")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Range */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label={t("admin.orders.startDate")}
                  value={filters.startDate || ""}
                  onChange={(e) => handleFilterChange("startDate", e.target.value || undefined)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label={t("admin.orders.endDate")}
                  value={filters.endDate || ""}
                  onChange={(e) => handleFilterChange("endDate", e.target.value || undefined)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>

              {/* Amount Range */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label={t("admin.orders.minAmount")}
                  value={filters.minAmount ?? ""}
                  onChange={(e) => handleFilterChange("minAmount", e.target.value ? parseFloat(e.target.value) : undefined)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label={t("admin.orders.maxAmount")}
                  value={filters.maxAmount ?? ""}
                  onChange={(e) => handleFilterChange("maxAmount", e.target.value ? parseFloat(e.target.value) : undefined)}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  size="small"
                />
              </Grid>

              {/* Clear Filters */}
              <Grid item xs={12} sm={6} md={3}>
                <Button variant="outlined" color="secondary" onClick={clearFilters} fullWidth>
                  {t("admin.orders.clearFilters")}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* View Tabs: Active / Archived / Deleted */}
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Chip
            label={t("admin.orders.activeOrders")}
            color={!filters.archivedOnly && !filters.includeDeleted ? "primary" : "default"}
            variant={!filters.archivedOnly && !filters.includeDeleted ? "filled" : "outlined"}
            onClick={() => setFilters(f => ({ ...f, archivedOnly: undefined, includeDeleted: undefined, includeArchived: undefined, pageNumber: 1 }))}
          />
          <Chip
            label={t("admin.orders.archivedOrders")}
            color={filters.archivedOnly ? "warning" : "default"}
            variant={filters.archivedOnly ? "filled" : "outlined"}
            onClick={() => setFilters(f => ({ ...f, archivedOnly: true, includeDeleted: undefined, includeArchived: undefined, pageNumber: 1 }))}
          />
          <Chip
            label={t("admin.orders.deletedOrders")}
            color={filters.includeDeleted ? "error" : "default"}
            variant={filters.includeDeleted ? "filled" : "outlined"}
            onClick={() => setFilters(f => ({ ...f, includeDeleted: true, archivedOnly: undefined, includeArchived: undefined, pageNumber: 1 }))}
          />
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t("admin.orders.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.disabled" sx={{ fontFamily: "monospace", border: "1px solid", borderColor: "divider", borderRadius: 0.5, px: 0.5, fontSize: "0.7rem" }}>
                    Ctrl+K
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            {filters.search && (
              <Chip label={`${t("admin.orders.searchPlaceholder").replace("...", "")}: "${filters.search}"`} size="small" onDelete={() => handleRemoveFilter("search")} />
            )}
            {filters.status && (
              <Chip label={`${t("admin.orders.status")}: ${getFilterLabel("status", filters.status)}`} size="small" onDelete={() => handleRemoveFilter("status")} />
            )}
            {filters.paymentStatus && (
              <Chip label={`${t("admin.orders.paymentStatus")}: ${filters.paymentStatus}`} size="small" onDelete={() => handleRemoveFilter("paymentStatus")} />
            )}
            {filters.startDate && (
              <Chip label={`${t("admin.orders.startDate")}: ${filters.startDate}`} size="small" onDelete={() => handleRemoveFilter("startDate")} />
            )}
            {filters.endDate && (
              <Chip label={`${t("admin.orders.endDate")}: ${filters.endDate}`} size="small" onDelete={() => handleRemoveFilter("endDate")} />
            )}
            {filters.minAmount !== undefined && (
              <Chip label={`${t("admin.orders.minAmount")}: $${filters.minAmount}`} size="small" onDelete={() => handleRemoveFilter("minAmount")} />
            )}
            {filters.maxAmount !== undefined && (
              <Chip label={`${t("admin.orders.maxAmount")}: $${filters.maxAmount}`} size="small" onDelete={() => handleRemoveFilter("maxAmount")} />
            )}
            {filters.pickUpLocation && (
              <Chip label={`${t("admin.orders.pickupLocation")}: ${filters.pickUpLocation}`} size="small" onDelete={() => handleRemoveFilter("pickUpLocation")} />
            )}
            <Chip label={t("admin.orders.clearFilters")} size="small" variant="outlined" color="secondary" onClick={clearFilters} onDelete={clearFilters} deleteIcon={<CloseIcon />} />
          </Box>
        )}

        {/* Bulk Actions Bar */}
        {selectedOrderIds.length > 0 && (
          <Paper sx={{ p: 2, mb: 2, backgroundColor: "#e3f2fd" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="body2" fontWeight="bold">
                {t("admin.orders.ordersSelected", { count: selectedOrderIds.length })}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t("admin.orders.newStatus")}</InputLabel>
                <Select
                  value={bulkStatusId}
                  onChange={(e) => setBulkStatusId(Number(e.target.value))}
                  label={t("admin.orders.newStatus")}
                >
                  {orderStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={bulkUpdateItems}
                    onChange={(e) => {
                      setBulkUpdateItems(e.target.checked);
                      if (!e.target.checked) setBulkItemStatusId("");
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t("admin.orders.alsoUpdateItems")}
                  </Typography>
                }
              />
              {bulkUpdateItems && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>{t("admin.orders.itemStatus")}</InputLabel>
                  <Select
                    value={bulkItemStatusId}
                    onChange={(e) => setBulkItemStatusId(Number(e.target.value))}
                    label={t("admin.orders.itemStatus")}
                  >
                    {itemStatuses.map((status) => (
                      <MenuItem key={status.id} value={status.id}>
                        {status.displayName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {bulkUpdateItems && bulkItemStatusId && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={bulkSendNotification}
                      onChange={(e) => setBulkSendNotification(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t("admin.orders.sendNotificationToCustomers")}
                    </Typography>
                  }
                />
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleBulkStatusUpdate}
                disabled={bulkLoading || !bulkStatusId || (bulkUpdateItems && !bulkItemStatusId)}
              >
                {t("admin.orders.bulkUpdateStatus")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setSelectedOrderIds([]);
                  setBulkStatusId("");
                  setBulkItemStatusId("");
                  setBulkUpdateItems(false);
                  setBulkSendNotification(false);
                }}
              >
                {t("common.cancel")}
              </Button>
            </Box>
          </Paper>
        )}

        <Paper>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedOrderIds.length > 0 && selectedOrderIds.length < orders.length}
                      checked={orders.length > 0 && selectedOrderIds.length === orders.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  {[
                    { label: t("admin.orders.orderNumber"), key: "ordernumber" },
                    { label: t("admin.orders.customer"), key: "customer" },
                    { label: t("admin.orders.amount"), key: "totalprice" },
                    { label: t("admin.orders.status"), key: "status" },
                    { label: t("admin.orders.payment"), key: "paymentstatus" },
                    { label: t("admin.orders.cargoCompany"), key: "" },
                    { label: t("admin.orders.orderedDate"), key: "createdat" },
                    { label: t("admin.orders.items"), key: "" },
                    { label: t("admin.orders.actions"), key: "" },
                  ].map((col) =>
                    col.key ? (
                      <TableCell key={col.label}>
                        <TableSortLabel
                          active={filters.sortBy === col.key}
                          direction={filters.sortBy === col.key && !filters.sortDescending ? "asc" : "desc"}
                          onClick={() => handleSort(col.key)}
                          IconComponent={filters.sortBy === col.key && !filters.sortDescending ? ArrowUpward : ArrowDownward}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ) : (
                      <TableCell key={col.label}>{col.label}</TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  // Loading skeleton rows
                  Array.from({ length: rowsPerPage > 10 ? 10 : rowsPerPage }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      <TableCell padding="checkbox"><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
                      <TableCell><Skeleton width={80} /></TableCell>
                      <TableCell><Skeleton width={120} /><Skeleton width={160} height={14} /></TableCell>
                      <TableCell><Skeleton width={70} /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                      <TableCell><Skeleton width={90} /></TableCell>
                      <TableCell><Skeleton width={60} /></TableCell>
                      <TableCell><Skeleton width={30} /></TableCell>
                      <TableCell><Skeleton width={90} /></TableCell>
                    </TableRow>
                  ))
                ) : orders.length === 0 ? (
                  // Empty state
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <SearchIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                      <Typography variant="h6" color="text.secondary">
                        {t("admin.orders.noOrdersFound")}
                      </Typography>
                      <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                        {t("admin.orders.tryAdjustingFilters")}
                      </Typography>
                      {hasActiveFilters && (
                        <Button variant="outlined" size="small" onClick={clearFilters}>
                          {t("admin.orders.clearFilters")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order) => (
                  <TableRow
                    key={order.id}
                    selected={selectedOrderIds.includes(order.orderNumber)}
                    hover
                    sx={{
                      cursor: "pointer",
                      ...(order.isDeleted ? { opacity: 0.5, textDecoration: "line-through", bgcolor: "action.hover" } : {}),
                      ...(order.isArchived && !order.isDeleted ? { opacity: 0.7, bgcolor: "action.hover" } : {}),
                    }}
                    onClick={(e) => {
                      // Don't navigate if clicking on interactive elements
                      const target = e.target as HTMLElement;
                      if (target.closest("button, a, input, .MuiChip-root, .MuiCheckbox-root, .MuiIconButton-root")) return;
                      navigate(`/admin/orders/${order.id}`);
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedOrderIds.includes(order.orderNumber)}
                        onChange={() => handleSelectOrder(order.orderNumber)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                          color: "primary.main",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {order.orderNumber}
                      </Typography>
                      {order.isDeleted && <Chip label={t("admin.orders.deletedBadge")} size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: "0.65rem", ml: 0.5 }} />}
                      {order.isArchived && !order.isDeleted && <Chip label={t("admin.orders.archivedBadge")} size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: "0.65rem", ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {order.fullName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {order.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {t("admin.orders.total")}:{" "}
                        <Currency amount={order.totalPrice} tjsAmount={order.totalPriceTjs} />
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t("admin.orders.shipping")}:{" "}
                        <Currency amount={order.shippingCost} tjsAmount={order.shippingCostTjs} />
                        <br />
                        {t("admin.orders.tax")}:{" "}
                        <Currency amount={order.tax} />
                        <br />
                        {t("admin.orders.fees")}:{" "}
                        <Currency amount={order.fees ?? 0} />
                      </Typography>
                      {order.promotionCode && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={t("admin.orders.promo", { code: order.promotionCode })}
                            size="small"
                            sx={{
                              backgroundColor: "#e8f5e9",
                              color: "#2e7d32",
                              fontFamily: "monospace",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: 20,
                            }}
                          />
                          {order.promotionDiscountAmount && (
                            <Typography
                              variant="caption"
                              color="success.main"
                              sx={{ display: "block", mt: 0.25 }}
                            >
                              {t("admin.orders.saved")}:{" "}
                              <NoTranslate as="span">
                                {`$${order.promotionDiscountAmount.toFixed(2)}`}
                              </NoTranslate>
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        size="small"
                        color={getStatusColor(order.status) as any}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusClick(order.id, order.orderNumber, order.status);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { opacity: 0.8 },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Chip
                          label={order.paymentStatus}
                          size="small"
                          color={
                            getPaymentStatusColor(order.paymentStatus) as any
                          }
                          sx={{ mb: 0.5 }}
                        />
                        {order.paymentMethodType && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            {order.paymentMethodType}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {order.cargoCompanyName ? (
                        <Typography
                          variant="body2"
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            order.cargoCompanyId &&
                            navigate(
                              `/admin/cargo-companies/${order.cargoCompanyId}/users`
                            );
                          }}
                        >
                          {order.cargoCompanyName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t("admin.orders.unassigned")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${order.orderedDate} ${order.orderedDateTime}`} arrow>
                        <Typography variant="body2">
                          {getRelativeTime(order.orderedDate, t, order.orderedDateTime)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{order.itemCount}</Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewOrderDetails(order.orderNumber, order.id);
                        }}
                        title={t("admin.orders.viewDetails")}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditOrder(order);
                        }}
                        title={t("admin.orders.editOrder")}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, order.orderNumber);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              setStatusDialog({
                open: true,
                orderNumber: selectedOrder!,
                type: "status",
              });
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            {t("admin.orders.updateStatus")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              const order = orders.find((o) => o.orderNumber === selectedOrder);
              setPaymentStatusModal({
                open: true,
                order: order || null,
              });
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <PaymentIcon fontSize="small" />
            </ListItemIcon>
            {t("admin.orders.updatePaymentStatus")}
          </MenuItem>

          <MenuItem
            onClick={() => {
              setStatusDialog({
                open: true,
                orderNumber: selectedOrder!,
                type: "assign",
              });
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <AssignmentIcon fontSize="small" />
            </ListItemIcon>
            {t("admin.orders.assignOrder")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setStatusDialog({
                open: true,
                orderNumber: selectedOrder!,
                type: "note",
              });
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <NoteIcon fontSize="small" />
            </ListItemIcon>
            {t("admin.orders.addNote")}
          </MenuItem>
          {(() => {
            const order = orders.find((o) => o.orderNumber === selectedOrder);
            if (!order) return null;
            return (
              <>
                {order.isArchived ? (
                  <MenuItem onClick={() => { handleUnarchiveOrder(order.id); handleMenuClose(); }}>
                    <ListItemIcon><UnarchiveIcon fontSize="small" /></ListItemIcon>
                    {t("admin.orders.unarchiveOrder")}
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => { setArchiveConfirm({ open: true, orderId: order.id, orderNumber: order.orderNumber || order.id, loading: false }); handleMenuClose(); }}>
                    <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
                    {t("admin.orders.archiveOrder")}
                  </MenuItem>
                )}
                {order.isDeleted ? (
                  <MenuItem onClick={() => { handleRestoreOrder(order.id); handleMenuClose(); }}>
                    <ListItemIcon><RestoreIcon fontSize="small" color="success" /></ListItemIcon>
                    {t("admin.orders.restoreOrder")}
                  </MenuItem>
                ) : (
                  <MenuItem onClick={() => { setDeleteConfirm({ open: true, orderId: order.id, orderNumber: order.orderNumber || order.id, loading: false }); handleMenuClose(); }} sx={{ color: "error.main" }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    {t("admin.orders.deleteOrder")}
                  </MenuItem>
                )}
              </>
            );
          })()}
        </Menu>

        {/* Delete Order Confirmation Dialog */}
        <Dialog
          open={deleteConfirm.open}
          onClose={() => !deleteConfirm.loading && setDeleteConfirm({ open: false, orderId: "", orderNumber: "", loading: false })}
        >
          <DialogTitle>{t("admin.orders.deleteOrder")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("admin.orders.confirmDeleteOrder", { orderNumber: deleteConfirm.orderNumber })}
            </DialogContentText>
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t("admin.orders.deleteOrderWarning")}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button disabled={deleteConfirm.loading} onClick={() => setDeleteConfirm({ open: false, orderId: "", orderNumber: "", loading: false })}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={deleteConfirm.loading}
              onClick={async () => {
                setDeleteConfirm(prev => ({ ...prev, loading: true }));
                await handleDeleteOrder(deleteConfirm.orderId);
                setDeleteConfirm({ open: false, orderId: "", orderNumber: "", loading: false });
              }}
              color="error"
              variant="contained"
            >
              {deleteConfirm.loading ? <CircularProgress size={20} /> : t("admin.orders.deleteOrder")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Archive Order Confirmation Dialog */}
        <Dialog
          open={archiveConfirm.open}
          onClose={() => !archiveConfirm.loading && setArchiveConfirm({ open: false, orderId: "", orderNumber: "", loading: false })}
        >
          <DialogTitle>{t("admin.orders.archiveOrder")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("admin.orders.confirmArchiveOrder", { orderNumber: archiveConfirm.orderNumber })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button disabled={archiveConfirm.loading} onClick={() => setArchiveConfirm({ open: false, orderId: "", orderNumber: "", loading: false })}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={archiveConfirm.loading}
              onClick={async () => {
                setArchiveConfirm(prev => ({ ...prev, loading: true }));
                await handleArchiveOrder(archiveConfirm.orderId);
                setArchiveConfirm({ open: false, orderId: "", orderNumber: "", loading: false });
              }}
              color="warning"
              variant="contained"
            >
              {archiveConfirm.loading ? <CircularProgress size={20} /> : t("admin.orders.archiveOrder")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Cargo Company Dialog */}
        {statusDialog.type === "assign" && (
          <Dialog
            open={statusDialog.open}
            onClose={() => setStatusDialog({ ...statusDialog, open: false })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{t("admin.orders.assignOrder")}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" mb={2}>
                {t("admin.orders.orderLabel")}: {statusDialog.orderNumber}
              </Typography>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel>{t("admin.orders.cargoCompany")}</InputLabel>
                <Select
                  value={statusDialog.orderNumber ? (orders.find((o) => o.orderNumber === statusDialog.orderNumber)?.cargoCompanyId || "") : ""}
                  label={t("admin.orders.cargoCompany")}
                  onChange={async (e) => {
                    const order = orders.find((o) => o.orderNumber === statusDialog.orderNumber);
                    if (!order) return;
                    try {
                      const cargoCompanyId = e.target.value || null;
                      await adminOrderService.updateOrderComprehensive(order.id, { cargoCompanyId });
                      showSnackbar(t("admin.orders.orderUpdated"), "success");
                      setStatusDialog({ ...statusDialog, open: false });
                      await loadOrders();
                    } catch {
                      showSnackbar(t("admin.orders.failedUpdateOrder"), "error");
                    }
                  }}
                >
                  <MenuItem value=""><em>{t("admin.orders.unassigned")}</em></MenuItem>
                  {cargoCompanies?.data?.map((company) => (
                    <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setStatusDialog({ ...statusDialog, open: false })}>
                {t("admin.orders.cancel")}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Add Note Dialog */}
        {statusDialog.type === "note" && (
          <Dialog
            open={statusDialog.open}
            onClose={() => setStatusDialog({ ...statusDialog, open: false })}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{t("admin.orders.addNote")}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" mb={2}>
                {t("admin.orders.orderLabel")}: {statusDialog.orderNumber}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder={t("admin.orders.addNotesPlaceholder")}
                sx={{ mt: 1 }}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setNoteText(""); setStatusDialog({ ...statusDialog, open: false }); }}>
                {t("admin.orders.cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  if (!noteText?.trim()) return;
                  const order = orders.find((o) => o.orderNumber === statusDialog.orderNumber);
                  if (!order) return;
                  try {
                    await adminOrderService.updateOrderComprehensive(order.id, { notes: noteText });
                    showSnackbar(t("admin.orders.orderUpdated"), "success");
                    setNoteText("");
                    setStatusDialog({ ...statusDialog, open: false });
                    await loadOrders();
                  } catch {
                    showSnackbar(t("admin.orders.failedUpdateOrder"), "error");
                  }
                }}
              >
                {t("admin.orders.update")}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Quick Status Update from context menu — reuse OrderStatusUpdateModal */}
        {statusDialog.type === "status" && statusDialog.open && (() => {
          const order = orders.find((o) => o.orderNumber === statusDialog.orderNumber);
          if (!order) return null;
          return (
            <OrderStatusUpdateModal
              open
              onClose={() => setStatusDialog({ ...statusDialog, open: false })}
              orderId={order.id}
              orderNumber={order.orderNumber}
              currentStatus={order.status}
              orderItems={[]}
              onStatusUpdate={handleOrderStatusUpdate}
            />
          );
        })()}

        {/* Payment Status Update Modal */}
        <PaymentStatusUpdateModal
          open={paymentStatusModal.open}
          onClose={() => {
            setPaymentStatusModal({ open: false, order: null });
          }}
          order={paymentStatusModal.order}
          onUpdate={() => {
            // Refresh orders list to show updated payment status
            loadOrders();
            showSnackbar(t("admin.orders.paymentStatusUpdated"), "success");
          }}
        />

        {/* Order Status Update Modal */}
        <OrderStatusUpdateModal
          open={orderStatusModal.open}
          onClose={() =>
            setOrderStatusModal({ ...orderStatusModal, open: false })
          }
          orderId={orderStatusModal.orderId}
          orderNumber={orderStatusModal.orderNumber}
          currentStatus={orderStatusModal.currentStatus}
          orderItems={orderStatusModal.orderItems}
          onStatusUpdate={handleOrderStatusUpdate}
        />

        {/* Edit Order Dialog */}
        <Dialog
          open={editOrderDialog.open}
          onClose={handleCloseEditDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {t("admin.orders.editOrder")} - {editOrderDialog.order?.orderNumber || t("admin.orders.loading")}
          </DialogTitle>
          <DialogContent>
            {editOrderDialog.loading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CircularProgress />
              </Box>
            )}

            {editOrderDialog.error && !editOrderDialog.loading && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{editOrderDialog.error}</Alert>
              </Box>
            )}

            {editOrderDialog.order && !editOrderDialog.loading && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("admin.orders.customerName")}
                      value={editOrderDialog.order.fullName}
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("admin.orders.customerEmail")}
                      value={editOrderDialog.order.email}
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("admin.orders.totalAmount")}
                      type="number"
                      value={editOrderDialog.order.totalPrice}
                      onChange={(e) => {
                        const updatedOrder = {
                          ...editOrderDialog.order!,
                          totalPrice: parseFloat(e.target.value) || 0,
                        };
                        setEditOrderDialog({
                          ...editOrderDialog,
                          order: updatedOrder,
                        });
                      }}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("admin.orders.shippingCost")}
                      type="number"
                      value={editOrderDialog.order.shippingCost}
                      onChange={(e) => {
                        const updatedOrder = {
                          ...editOrderDialog.order!,
                          shippingCost: parseFloat(e.target.value) || 0,
                        };
                        setEditOrderDialog({
                          ...editOrderDialog,
                          order: updatedOrder,
                        });
                      }}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("admin.orders.tax")}
                      type="number"
                      value={editOrderDialog.order.tax}
                      onChange={(e) => {
                        const updatedOrder = {
                          ...editOrderDialog.order!,
                          tax: parseFloat(e.target.value) || 0,
                        };
                        setEditOrderDialog({
                          ...editOrderDialog,
                          order: updatedOrder,
                        });
                      }}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("admin.orders.finalTotal")}
                      type="number"
                      value={editOrderDialog.order.finalTotal}
                      onChange={(e) => {
                        const updatedOrder = {
                          ...editOrderDialog.order!,
                          finalTotal: parseFloat(e.target.value) || 0,
                        };
                        setEditOrderDialog({
                          ...editOrderDialog,
                          order: updatedOrder,
                        });
                      }}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("admin.orders.totalCarrierCargoFee")}
                      type="number"
                      value={editOrderDialog.order.totalCarrierCargoFee || 0}
                      onChange={(e) => {
                        const updatedOrder = {
                          ...editOrderDialog.order!,
                          totalCarrierCargoFee: parseFloat(e.target.value) || 0,
                        };
                        setEditOrderDialog({
                          ...editOrderDialog,
                          order: updatedOrder,
                        });
                      }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("admin.orders.pickupLocation")}
                      value={editOrderDialog.order.pickupLocation || ""}
                      onChange={(e) => {
                        const updatedOrder = {
                          ...editOrderDialog.order!,
                          pickupLocation: e.target.value,
                        };
                        setEditOrderDialog({
                          ...editOrderDialog,
                          order: updatedOrder,
                        });
                      }}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t("admin.orders.paymentStatus")}</InputLabel>
                      <Select
                        value={
                          editOrderDialog.order?.paymentStatus ||
                          editOrderDialog.order?.paymentInfo?.paymentStatus ||
                          ""
                        }
                        label={t("admin.orders.paymentStatus")}
                        onChange={(e) => {
                          const updatedOrder = {
                            ...editOrderDialog.order!,
                            paymentStatus: e.target.value,
                            // Also update paymentInfo.paymentStatus for consistency
                            paymentInfo: {
                              ...editOrderDialog.order!.paymentInfo,
                              paymentStatus: e.target.value,
                            },
                          };
                          setEditOrderDialog({
                            ...editOrderDialog,
                            order: updatedOrder,
                          });
                        }}
                      >
                        {PAYMENT_STATUSES.map((status) => (
                          <MenuItem key={status} value={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>{t("admin.orders.cargoCompany")}</InputLabel>
                      <Select
                        value={editOrderDialog.order.cargoCompanyId || ""}
                        label={t("admin.orders.cargoCompany")}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          // Convert empty string to undefined to match type (AdminOrderDetail expects string | undefined)
                          const cargoCompanyId =
                            selectedId === "" ? undefined : selectedId;
                          const selectedCompany = cargoCompanies?.data?.find(
                            (c) => c.id === selectedId
                          );
                          const updatedOrder = {
                            ...editOrderDialog.order!,
                            cargoCompanyId: cargoCompanyId,
                            cargoCompanyName:
                              selectedCompany?.name || undefined,
                          };
                          setEditOrderDialog({
                            ...editOrderDialog,
                            order: updatedOrder,
                          });
                        }}
                      >
                        <MenuItem value="">
                          <em>{t("admin.orders.unassigned")}</em>
                        </MenuItem>
                        {cargoCompanies?.data?.map((company) => (
                          <MenuItem key={company.id} value={company.id}>
                            {company.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {editOrderDialog.order.notes !== undefined && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t("admin.orders.notes")}
                        multiline
                        rows={3}
                        value={editOrderDialog.order.notes || ""}
                        onChange={(e) => {
                          const updatedOrder = {
                            ...editOrderDialog.order!,
                            notes: e.target.value,
                          };
                          setEditOrderDialog({
                            ...editOrderDialog,
                            order: updatedOrder,
                          });
                        }}
                        variant="outlined"
                        helperText={t("admin.orders.notesHelperText")}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>{t("admin.orders.cancel")}</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSaveOrderEdit(editOrderDialog.order!)}
              disabled={!editOrderDialog.order || editOrderDialog.loading}
            >
              {t("admin.orders.updateOrder")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Order Details Modal */}
        <OrderDetailsModal
          open={orderDetailsModal.open}
          onClose={handleCloseOrderDetailsModal}
          orderNumber={orderDetailsModal.orderNumber || ""}
          orderId={orderDetailsModal.orderId || ""}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminPage>
  );
};

export default OrdersPage;
